import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';
import { Dialog } from './TodoPeople';
import './TodoClientsDeps.css';

/*
 * TO-DOS THAT WAIT ON OTHER TO-DOS (migration 072).
 *
 * Gordon: "blocked by". File the accounts only once the letter of
 * representation is signed; post the journals only once the review points
 * are cleared. So a to-do can WAIT ON others and HOLD UP others; its row says
 * "Waiting on …" with a lock while anything it waits on is open; ticking it
 * anyway asks first; and when the last thing it waits on is done, whoever has
 * it hears "ready to start" in the bell (the server's job - lib/todo-deps.js).
 *
 * A to-do the person cannot see is still counted ("and 1 you can't see") but
 * never named: a link is not a way into somebody else's private list.
 *
 * Repeating to-dos: the link stays on the series. A repeating blocker counts
 * as done for as long as its last tick is newer than the link (or than the
 * waiting to-do's own last tick), which is what "waits on this month's
 * payroll" means.
 */

/** The lock, drawn like the rest of the set (16px, 1.5 stroke, currentColor). */
export const LockIcon = ({ size = 12 }) => (
  <svg className="td-ic tdd-lock-ic" viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor"
       strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="3.25" y="7" width="9.5" height="7" rx="1.5" />
    <path d="M5.25 7V5a2.75 2.75 0 0 1 5.5 0v2" />
  </svg>
);

/** "“Get the letter”" / "2 to-dos" / "a to-do you can't see": what a to-do waits on, in words. */
export function waitingWords(waiting) {
  const list = Array.isArray(waiting) ? waiting : [];
  if (!list.length) return '';
  const named = list.filter((w) => w && w.title);
  if (list.length === 1) return named.length ? `“${named[0].title}”` : 'a to-do you can’t see';
  return `${list.length} to-dos`;
}

/** The row's chip: a lock and "Waiting on …". A button that opens the detail. */
export function WaitingChip({ item, onOpen = null }) {
  const waiting = Array.isArray(item?.waiting_on) ? item.waiting_on : [];
  if (!waiting.length) return null;
  const words = waitingWords(waiting);
  const all = waiting.map((w) => (w.title ? `“${w.title}”` : 'a to-do you can’t see')).join(', ');
  const body = (
    <>
      <LockIcon />
      <span className="tdd-wait-text">Waiting on {words}</span>
    </>
  );
  if (!onOpen) return <span className="tdd-wait" data-waiting={waiting.length} title={`Waiting on ${all}`}>{body}</span>;
  return (
    <button type="button" className="tdd-wait" data-waiting={waiting.length} title={`Waiting on ${all}`}
            aria-label={`Waiting on ${all}. Show what it waits on`} onClick={() => onOpen(item)}>{body}</button>
  );
}

/**
 * "Complete anyway?" - ticking a to-do that still waits on something is
 * allowed, but asked about first. Enter (the default button) completes;
 * Escape or "Not yet" leaves it.
 */
export function AnywayDialog({ item, onConfirm, onClose }) {
  const waiting = Array.isArray(item?.waiting_on) ? item.waiting_on : [];
  const named = waiting.filter((w) => w.title);
  const unseen = waiting.length - named.length;
  let words;
  if (waiting.length === 1) {
    words = named.length ? `“${named[0].title}” is still open – complete anyway?`
      : 'It is still waiting on a to-do you can’t see – complete anyway?';
  } else {
    const list = named.map((w) => `“${w.title}”`);
    if (unseen) list.push(`${unseen} you can’t see`);
    words = `It is still waiting on ${waiting.length} to-dos: ${list.join(', ')}. Complete anyway?`;
  }
  return (
    <Dialog label={`“${item.title}” is still waiting`} onClose={onClose} className="tdd-anyway">
      <div className="tdd-anyway-body">
        <p className="tdd-anyway-text"><LockIcon size={16} /><span>{words}</span></p>
        <div className="tdd-anyway-buttons">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Not yet</button>
          <button type="button" className="btn btn-primary" autoFocus onClick={onConfirm}>Complete anyway</button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Find a to-do to link: a search over the to-dos the page already has (the
 * person's own lists - what they can see), as a list of buttons under the
 * box. Arrow keys move through it, Enter picks, Escape closes.
 */
function Finder({ candidates, exclude, label, onPick, onClose }) {
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const box = useRef(null);
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (candidates || [])
      .filter((c) => c && c.id && !exclude.has(c.id) && (!t || String(c.title || '').toLowerCase().includes(t)))
      .slice(0, 8);
  }, [candidates, exclude, q]);
  useEffect(() => { setAt(0); }, [q]);
  useEffect(() => { if (box.current) box.current.focus(); }, []);
  return (
    <div className="tdd-find" onKeyDown={(e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => Math.min(hits.length - 1, i + 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
      if (e.key === 'Enter' && e.target === box.current) { e.preventDefault(); if (hits[at]) onPick(hits[at]); }
    }}>
      <input ref={box} type="search" value={q} aria-label={label} placeholder="Find a to-do…"
             role="combobox" aria-expanded={hits.length > 0} aria-controls="tdd-find-list"
             aria-activedescendant={hits[at] ? `tdd-hit-${hits[at].id}` : undefined}
             onChange={(e) => setQ(e.target.value)} />
      {hits.length === 0 ? (
        <p className="tds-quiet">{q.trim() ? 'No open to-do by that name.' : 'No other open to-dos.'}</p>
      ) : (
        <ul className="tdd-find-list" id="tdd-find-list" role="listbox" aria-label={label}>
          {hits.map((c, i) => (
            <li key={c.id} id={`tdd-hit-${c.id}`} role="option" aria-selected={i === at}>
              <button type="button" className={`tdd-find-item${i === at ? ' is-on' : ''}`} data-find={c.id}
                      tabIndex={-1} onMouseEnter={() => setAt(i)} onClick={() => onPick(c)}>
                <span className="tdd-find-title">{c.title}</span>
                {c.listName && <span className="tdd-find-list-name">{c.listName}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="tds-link tdd-find-cancel" onClick={onClose}>Cancel</button>
    </div>
  );
}

/** One side: "Waiting on" or "Holding up", its rows and its + Add. */
function Side({ kind, rows, busy, adding, onAdd, onRemove, onCancel, candidates, exclude, onPick }) {
  const waiting = kind === 'blocked_by';
  const heading = waiting ? 'Waiting on' : 'Holding up';
  const open = rows.filter((r) => r.open).length;
  return (
    <section className={`tdd-side is-${waiting ? 'waiting' : 'holding'}`} data-deps={kind}
             aria-label={heading}>
      <div className="tdd-side-top">
        <h4 className="tdd-side-head">
          {waiting && open > 0 && <LockIcon />}
          {heading}
          {rows.length > 0 && <span className="tdd-side-n">{waiting && open ? `${open} open` : rows.length}</span>}
        </h4>
        {!adding && (
          <button type="button" className="tds-link tdd-add" disabled={busy} onClick={onAdd}
                  aria-label={waiting ? 'Add what it waits on' : 'Add what waits on it'}>
            + {waiting ? 'Waits on…' : 'Holds up…'}
          </button>
        )}
      </div>
      {!rows.length && !adding && (
        <p className="tdd-none">{waiting ? 'Nothing - it can start any time.' : 'Nothing waits on it.'}</p>
      )}
      {rows.length > 0 && (
        <ul className="tdd-rows">
          {rows.map((r) => (
            <li key={r.id} className={`tdd-row${r.open ? ' is-open' : ' is-done'}${r.visible ? '' : ' is-hidden'}`}
                data-dep={r.id}>
              <span className="tdd-state" aria-hidden="true">
                {r.open ? <LockIcon /> : <Icon name="check" size={12} />}
              </span>
              <span className="tdd-row-main">
                <span className="tdd-row-title">{r.visible ? r.title : 'A to-do you can’t see'}</span>
                {r.visible && (r.list_name || r.assignee_name) && (
                  <span className="tdd-row-meta">
                    {[r.list_name, r.assignee_name].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              <span className={`tdd-row-state is-${r.open ? 'open' : 'done'}`}>{r.open ? 'Open' : 'Done'}</span>
              {r.can_remove && (
                <button type="button" className="tds-icon-btn tdd-remove" disabled={busy}
                        aria-label={`${waiting ? 'Stop waiting on' : 'Stop holding up'} ${r.visible ? `“${r.title}”` : 'that to-do'}`}
                        title="Take this link off" onClick={() => onRemove(r)}><Icon name="close" size={14} /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && (
        <Finder candidates={candidates} exclude={exclude} onPick={onPick} onClose={onCancel}
                label={waiting ? 'Find what it waits on' : 'Find what waits on it'} />
      )}
    </section>
  );
}

/**
 * The detail's "Waiting on" and "Holding up". `api` is the page's (/todos,
 * with `as=`); `candidates` the open to-dos the page has ({ id, title,
 * listName }); `onChanged` reads the list again so the rows' locks follow.
 */
export default function TodoDeps({ item, api, candidates = [], busy = false, onChanged = null }) {
  const [deps, setDeps] = useState(null);
  const [adding, setAdding] = useState(null);   // 'blocked_by' | 'blocking'
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const id = item.id;

  const load = useCallback(() => api.get(`/${id}/dependencies`)
    .then((r) => setDeps({ blocked_by: r.data?.blocked_by || [], blocking: r.data?.blocking || [] }))
    .catch((err) => {
      setDeps({ blocked_by: [], blocking: [] });
      if (err.response?.status !== 503) setProblem(err.response?.data?.error || 'Could not load what it waits on');
    }), [api, id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setSaving(true);
    setProblem(null);
    try {
      const r = await fn();
      if (r?.data) setDeps({ blocked_by: r.data.blocked_by || [], blocking: r.data.blocking || [] });
      setAdding(null);
      if (onChanged) onChanged();
    } catch (err) {
      setProblem(err.response?.data?.error || 'That did not go through');
    } finally {
      setSaving(false);
    }
  };

  const exclude = useMemo(() => new Set([id, ...((deps?.blocked_by || []).map((r) => r.id)),
    ...((deps?.blocking || []).map((r) => r.id))]), [id, deps]);

  if (deps === null) return <div className="tdd" data-deps-for={id}><p className="tds-quiet">Loading…</p></div>;
  const pick = (side) => (c) => run(() => api.post(`/${id}/${side === 'blocked_by' ? 'blocked-by' : 'blocking'}`, { id: c.id }));
  const remove = (side) => (r) => run(() => api.del(`/${id}/${side === 'blocked_by' ? 'blocked-by' : 'blocking'}/${r.id}`));

  return (
    <div className="tdd" data-deps-for={id}>
      {problem && <p className="tds-error" role="alert">{problem}</p>}
      {['blocked_by', 'blocking'].map((side) => (
        <Side key={side} kind={side} rows={deps[side]} busy={busy || saving} adding={adding === side}
              candidates={candidates} exclude={exclude}
              onAdd={() => { setProblem(null); setAdding(side); }} onCancel={() => setAdding(null)}
              onPick={pick(side)} onRemove={remove(side)} />
      ))}
    </div>
  );
}
